import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const STEPS = [
  ['Pick a category', 'Tap a picture at the top to show only those activities, or search by name.'],
  ['Choose an activity', 'Click an activity to see its levels and how far you have got.'],
  ['Press Play', 'GCompris opens on this computer at your next level.'],
  ['Close GCompris when you finish', 'You come back here and your progress is saved. Every new level you complete can earn coins.'],
];

export default function GComprisHelpModal({ onClose }) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="gcompris-help-title"
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 id="gcompris-help-title" className="text-2xl font-extrabold text-slate-900">How to use GCompris</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close help"
            className="shrink-0 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={22} />
          </button>
        </div>

        <ol className="flex flex-col gap-3">
          {STEPS.map(([heading, detail], index) => (
            <li key={heading} className="flex items-start gap-3">
              <span className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-indigo-100 text-sm font-extrabold text-indigo-700 flex items-center justify-center">
                {index + 1}
              </span>
              <div>
                <p className="font-bold text-slate-900">{heading}</p>
                <p className="text-sm font-medium text-slate-600">{detail}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
          First time on this computer? Run the EduBridge registration from the POC once as administrator, and make sure GCompris is installed.
        </div>

        <p className="mt-4 text-xs font-medium text-gray-400">
          GCompris is free software from the KDE community (gcompris.net). The activities and their pictures belong to its authors.
        </p>
      </div>
    </div>,
    document.body
  );
}
